import { useState, useEffect } from 'react';
import { issueShare } from '../../utils/apiContentSharing';
import { isEmailRecipient } from '../../utils/sharesFields';
import { logout } from '../../utils/auth';
import OfflineHint, { useOnlineOnly } from '../OfflineHint';

/**
 * ShareContentModal - Enhanced share modal for content sharing
 *
 * Supports sharing books and chapters with a username or an email address,
 * plus an optional personal message. Differentiates between inviting new users
 * vs sharing with existing users.
 *
 * Props:
 * - isOpen: boolean - Controls modal visibility
 * - contentType: string - "book" or "chapter"
 * - contentId: string - UUID of the book (file) or chapter
 * - onClose: function - Callback to close the modal
 * - showContentTypeSelector: boolean (optional) - Whether to show book/chapter radio buttons
 * - bookId: string (optional) - Book UUID when sharing a chapter (for context)
 */
function ShareContentModal({
  isOpen,
  contentType: initialContentType,
  contentId: initialContentId,
  onClose,
  showContentTypeSelector = false,
  bookId = null
}) {
  const offline = useOnlineOnly();
  // Form state
  const [contentType, setContentType] = useState(initialContentType);
  const [contentId, setContentId] = useState(initialContentId);
  const [recipient, setRecipient] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');

  // UI state
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [recipientError, setRecipientError] = useState('');

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Reset form fields whenever the modal is opened or props change
  useEffect(() => {
    if (isOpen) {
      setContentType(initialContentType);
      setContentId(initialContentId);
      setRecipient('');
      setPersonalMessage('');
      setShareError('');
      setShareSuccess('');
      setRecipientError('');
    }
  }, [isOpen, initialContentType, initialContentId]);

  if (!isOpen) return null;

  // A recipient is either a username or an address; the "@" decides which,
  // matching the field issueShare will send it in.
  const validateRecipient = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'Username or email is required';
    }
    if (isEmailRecipient(trimmed)) {
      return emailRegex.test(trimmed) ? '' : 'Please enter a valid email address';
    }
    return trimmed.length > 50 ? 'Username must be 50 characters or fewer' : '';
  };

  const handleRecipientChange = (e) => {
    const next = e.target.value;
    setRecipient(next);

    // Clear the error as soon as the input becomes valid
    if (recipientError && next) {
      setRecipientError(validateRecipient(next));
    }
  };

  const handleContentTypeChange = (type) => {
    setContentType(type);
    // When switching from chapter to book, use the bookId if available
    if (type === 'book' && bookId) {
      setContentId(bookId);
    } else if (type === 'chapter') {
      setContentId(initialContentId);
    }
  };

  const getErrorMessage = (err) => {
    if (err.response) {
      const { status, data } = err.response;
      if (status === 401) {
        logout();
        return 'Unauthorized: Please login again.';
      }
      const message = data.message || data.detail;
      // 404 is either the project or, now, an unknown username — the backend
      // says which, so pass its wording through.
      if (status === 404) return message || 'Content not found.';

      if (status === 400) {
        return message || 'Invalid request. Please check your input.';
      }
      return message || 'Failed to share content. Please try again.';
    }
    if (err.request) return 'Network error: Could not connect to server';
    if (err.message) return err.message;
    return 'An unexpected error occurred';
  };

  const handleShareSubmit = async (e) => {
    e.preventDefault();

    const recipientValidationError = validateRecipient(recipient);
    if (recipientValidationError) {
      setRecipientError(recipientValidationError);
      return;
    }

    setShareLoading(true);
    setShareError('');
    setShareSuccess('');
    setRecipientError('');

    try {
      // All shares are book-scoped. When the caller is sharing a chapter,
      // we pass the parent book id as file_id and the chapter id as the
      // highlight to deep-link the recipient on first open.
      const fileIdForShare =
        contentType === 'chapter' ? bookId : (bookId || contentId);
      const highlightChapterId =
        contentType === 'chapter' ? contentId : null;

      const response = await issueShare(
        fileIdForShare,
        recipient.trim(),
        personalMessage.trim() || null,
        highlightChapterId,
      );

      // Different success message based on whether recipient exists
      const successMessage = response.recipient_account_exists
        ? `Successfully shared with ${recipient}!`
        : `Invitation sent to ${recipient}!`;

      setShareSuccess(successMessage);

      // Clear form
      setRecipient('');
      setPersonalMessage('');

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setShareSuccess('');
      }, 2000);
    } catch (err) {
      setShareError(getErrorMessage(err));
    } finally {
      setShareLoading(false);
    }
  };

  const remainingChars = 1000 - personalMessage.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Share Content
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleShareSubmit}>
          {/* Content Type Selector (if enabled) */}
          {showContentTypeSelector && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What would you like to share?
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="contentType"
                    value="book"
                    checked={contentType === 'book'}
                    onChange={(e) => handleContentTypeChange(e.target.value)}
                    className="mr-2 text-primary focus:ring-primary"
                    disabled={shareLoading}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Entire book</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="contentType"
                    value="chapter"
                    checked={contentType === 'chapter'}
                    onChange={(e) => handleContentTypeChange(e.target.value)}
                    className="mr-2 text-primary focus:ring-primary"
                    disabled={shareLoading}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">This chapter</span>
                </label>
              </div>
              {contentType === 'chapter' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Sharing a chapter also grants the recipient access to the entire book.
                </p>
              )}
            </div>
          )}

          {/* Recipient Input */}
          <div className="mb-4">
            <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipient username or email
            </label>
            <input
              type="text"
              id="recipient"
              value={recipient}
              onChange={handleRecipientChange}
              onBlur={() => {
                if (recipient) {
                  setRecipientError(validateRecipient(recipient));
                }
              }}
              placeholder="username or user@example.com"
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary ${
                recipientError
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              disabled={shareLoading}
              required
            />
            {recipientError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{recipientError}</p>
            )}
          </div>

          {/* Personal Message (optional) */}
          <div className="mb-4">
            <label htmlFor="personalMessage" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Personal Message (optional)
            </label>
            <textarea
              id="personalMessage"
              value={personalMessage}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  setPersonalMessage(e.target.value);
                }
              }}
              placeholder="Add a personal note..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              disabled={shareLoading}
            />
            <div className="flex justify-end mt-1">
              <span className={`text-xs ${remainingChars < 100 ? 'text-orange-500' : 'text-gray-500 dark:text-gray-400'}`}>
                {remainingChars} characters remaining
              </span>
            </div>
          </div>

          {/* Error Message */}
          {shareError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{shareError}</p>
            </div>
          )}

          {/* Success Message */}
          {shareSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">{shareSuccess}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={shareLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={shareLoading || !!recipientError || offline}
            >
              {shareLoading ? 'Sharing...' : 'Share'}
            </button>
          </div>
          <OfflineHint action="Sharing" />
        </form>
      </div>
    </div>
  );
}

export default ShareContentModal;
